import { z } from "zod";
import type { MotionRole } from "./define-motion-kind";
import {
	getMotionKind,
	isMotionKindKey,
	MAX_MOTION_MS,
	STILL_KIND_KEY,
} from "./registry";

/**
 * Motion settings (D10). One partial object per layer: Topic, Card and Block (`motionSettings`
 * JSON, the deck layer) and the remix timeline override. Every property inherits from the layer
 * below unless set, so a card can override only a duration and keep the topic's kind.
 */
export const MOTION_FIELD_ROLES = {
	cardIn: "transition",
	cardOut: "transition",
	blockIn: "animation",
	blockOut: "animation",
	transitionBetweenBeats: "transition",
} as const satisfies Record<string, MotionRole>;

export type MotionFieldKey = keyof typeof MOTION_FIELD_ROLES;

export const MOTION_FIELD_KEYS = Object.keys(
	MOTION_FIELD_ROLES,
) as MotionFieldKey[];

/** On/off switches: card transitions and block animations (the topic's former kill-switches). */
export const MOTION_TOGGLE_KEYS = ["cardsEnabled", "blocksEnabled"] as const;

export type MotionToggleKey = (typeof MOTION_TOGGLE_KEYS)[number];

const TOGGLE_FIELDS: Record<MotionToggleKey, readonly MotionFieldKey[]> = {
	cardsEnabled: ["cardIn", "cardOut"],
	blocksEnabled: ["blockIn", "blockOut"],
};

export const motionDurationMsSchema = z
	.number()
	.int()
	.min(0)
	.max(MAX_MOTION_MS);

export const motionFieldSchema = z
	.object({
		kindKey: z.string().min(1).optional(),
		durationMs: motionDurationMsSchema.optional(),
		params: z.record(z.string(), z.unknown()).optional(),
	})
	.strict();

export type MotionField = z.infer<typeof motionFieldSchema>;

function withKindKeyCheck<T extends z.ZodTypeAny>(schema: T) {
	return schema.superRefine((value, ctx) => {
		const record = value as Record<string, unknown> | null | undefined;
		if (!record) {
			return;
		}
		for (const field of MOTION_FIELD_KEYS) {
			const entry = record[field] as
				| { kindKey?: string | null; params?: unknown }
				| null
				| undefined;
			const kindKey = entry?.kindKey;
			if (typeof kindKey !== "string") {
				continue;
			}
			const role = MOTION_FIELD_ROLES[field];
			if (!isMotionKindKey(role, kindKey)) {
				ctx.addIssue({
					code: "custom",
					message: `${field}: unknown ${role} kind "${kindKey}"`,
					path: [field, "kindKey"],
				});
				continue;
			}
			if (entry?.params != null) {
				const parsed = getMotionKind(
					role,
					kindKey,
				).paramsSchema.safeParse(entry.params);
				if (!parsed.success) {
					ctx.addIssue({
						code: "custom",
						message: `${field}: invalid params for "${kindKey}"`,
						path: [field, "params"],
					});
				}
			}
		}
	});
}

export const motionSettingsV1Schema = withKindKeyCheck(
	z
		.object({
			cardsEnabled: z.boolean().optional(),
			blocksEnabled: z.boolean().optional(),
			cardIn: motionFieldSchema.optional(),
			cardOut: motionFieldSchema.optional(),
			blockIn: motionFieldSchema.optional(),
			blockOut: motionFieldSchema.optional(),
			transitionBetweenBeats: motionFieldSchema.optional(),
		})
		.strict(),
);

export type MotionSettingsV1 = z.infer<typeof motionSettingsV1Schema>;

/** A layer write: a value sets it, `null` clears it (inherit), absent leaves it. */
export const motionSettingsPatchSchema = withKindKeyCheck(
	z
		.object({
			cardsEnabled: z.boolean().nullable().optional(),
			blocksEnabled: z.boolean().nullable().optional(),
			...Object.fromEntries(
				MOTION_FIELD_KEYS.map((field) => [
					field,
					z
						.object({
							kindKey: z.string().min(1).nullable().optional(),
							durationMs: motionDurationMsSchema
								.nullable()
								.optional(),
							params: z
								.record(z.string(), z.unknown())
								.nullable()
								.optional(),
						})
						.strict()
						.nullable()
						.optional(),
				]),
			),
		})
		.strict(),
);

export type MotionSettingsPatch = {
	cardsEnabled?: boolean | null;
	blocksEnabled?: boolean | null;
} & {
	[K in MotionFieldKey]?: {
		kindKey?: string | null;
		durationMs?: number | null;
		params?: Record<string, unknown> | null;
	} | null;
};

/** Stored value for a layer: invalid or empty → null (the column stays unset). */
export function parseMotionSettings(raw: unknown): MotionSettingsV1 | null {
	if (raw == null) {
		return null;
	}
	const parsed = motionSettingsV1Schema.safeParse(raw);
	return parsed.success ? parsed.data : null;
}

function compactField(field: MotionField): MotionField | undefined {
	const next: MotionField = {};
	if (field.kindKey !== undefined) {
		next.kindKey = field.kindKey;
	}
	if (field.durationMs !== undefined) {
		next.durationMs = field.durationMs;
	}
	if (field.params !== undefined) {
		next.params = field.params;
	}
	return Object.keys(next).length > 0 ? next : undefined;
}

/** A patch that would leave the layer invalid (e.g. params that don't fit the field's kind). */
export class MotionSettingsWriteError extends Error {
	constructor(readonly issues: string[]) {
		super(`Invalid motion settings: ${issues.join("; ")}`);
		this.name = "MotionSettingsWriteError";
	}
}

/**
 * Applies a patch to one layer; returns null when nothing is left set. A new kind without params
 * drops the old kind's params. Throws `MotionSettingsWriteError` when the result is invalid.
 */
export function applyMotionSettingsPatch(
	current: MotionSettingsV1 | null | undefined,
	patch: MotionSettingsPatch,
): MotionSettingsV1 | null {
	const next: Record<string, unknown> = { ...(current ?? {}) };
	for (const toggle of MOTION_TOGGLE_KEYS) {
		const value = patch[toggle];
		if (value === null) {
			delete next[toggle];
		} else if (value !== undefined) {
			next[toggle] = value;
		}
	}
	for (const field of MOTION_FIELD_KEYS) {
		const value = patch[field];
		if (value === undefined) {
			continue;
		}
		if (value === null) {
			delete next[field];
			continue;
		}
		const previous = (next[field] as MotionField | undefined) ?? {};
		const merged: Record<string, unknown> = { ...previous };
		if (
			value.kindKey !== undefined &&
			value.kindKey !== previous.kindKey &&
			value.params === undefined
		) {
			delete merged.params;
		}
		for (const prop of ["kindKey", "durationMs", "params"] as const) {
			const propValue = value[prop];
			if (propValue === null) {
				delete merged[prop];
			} else if (propValue !== undefined) {
				merged[prop] = propValue;
			}
		}
		const compacted = compactField(merged as MotionField);
		if (compacted) {
			next[field] = compacted;
		} else {
			delete next[field];
		}
	}
	const parsed = motionSettingsV1Schema.safeParse(next);
	if (!parsed.success) {
		throw new MotionSettingsWriteError(
			parsed.error.issues.map((issue) => issue.message),
		);
	}
	return Object.keys(parsed.data).length > 0 ? parsed.data : null;
}

/**
 * The patch that undoes `patch` on `current`: every key and property the patch touches is put back
 * (`null` where it was unset). Applying it to the patched layer gives `current` again.
 */
export function invertMotionSettingsPatch(
	current: MotionSettingsV1 | null | undefined,
	patch: MotionSettingsPatch,
): MotionSettingsPatch {
	const inverse: MotionSettingsPatch = {};
	for (const toggle of MOTION_TOGGLE_KEYS) {
		if (patch[toggle] !== undefined) {
			inverse[toggle] = current?.[toggle] ?? null;
		}
	}
	for (const field of MOTION_FIELD_KEYS) {
		const value = patch[field];
		if (value === undefined) {
			continue;
		}
		const previous = current?.[field];
		if (value === null || !previous) {
			inverse[field] = previous ? { ...previous } : null;
			continue;
		}
		const fieldInverse: NonNullable<MotionSettingsPatch[MotionFieldKey]> =
			{};
		for (const prop of ["kindKey", "durationMs", "params"] as const) {
			if (value[prop] !== undefined) {
				fieldInverse[prop] = (previous[prop] ?? null) as never;
			}
		}
		// A kind change drops the old params implicitly, so undo puts them back.
		if (
			value.kindKey !== undefined &&
			value.params === undefined &&
			previous.params !== undefined
		) {
			fieldInverse.params = previous.params;
		}
		inverse[field] = fieldInverse;
	}
	return inverse;
}

export const MOTION_LAYERS = [
	"registry",
	"topic",
	"card",
	"block",
	"remix",
] as const;

export type MotionLayer = (typeof MOTION_LAYERS)[number];

const LAYER_ORDER = ["topic", "card", "block", "remix"] as const;

const motionLayerSchema = z.enum(MOTION_LAYERS);

export const resolvedMotionFieldSchema = z.object({
	kindKey: z.string(),
	durationMs: motionDurationMsSchema,
	params: z.record(z.string(), z.unknown()),
	/** Which layer supplied each property (inspector badges, R11). */
	source: z.object({
		kindKey: motionLayerSchema,
		durationMs: motionLayerSchema,
		params: motionLayerSchema,
		enabled: motionLayerSchema,
	}),
});

export type ResolvedMotionField = z.infer<typeof resolvedMotionFieldSchema>;

export const resolvedMotionSettingsSchema = z.object({
	cardIn: resolvedMotionFieldSchema,
	cardOut: resolvedMotionFieldSchema,
	blockIn: resolvedMotionFieldSchema,
	blockOut: resolvedMotionFieldSchema,
	transitionBetweenBeats: resolvedMotionFieldSchema,
});

export type ResolvedMotionSettings = Record<
	MotionFieldKey,
	ResolvedMotionField
>;

/**
 * Registry defaults: what a topic with nothing set plays (the pre-registry column defaults: card
 * in fade 300 ms, card out cut, block in fade 300 ms, block out none; beats cut).
 */
export const MOTION_REGISTRY_DEFAULTS: Record<
	MotionFieldKey,
	{ kindKey: string; durationMs: number }
> = {
	cardIn: { kindKey: "fade", durationMs: 300 },
	cardOut: { kindKey: "cut", durationMs: 300 },
	blockIn: { kindKey: "fade", durationMs: 300 },
	blockOut: { kindKey: "none", durationMs: 300 },
	transitionBetweenBeats: { kindKey: "cut", durationMs: 300 },
};

function toggleFor(field: MotionFieldKey): MotionToggleKey | null {
	for (const toggle of MOTION_TOGGLE_KEYS) {
		if (TOGGLE_FIELDS[toggle].includes(field)) {
			return toggle;
		}
	}
	return null;
}

/**
 * The one motion settings chain (R11): registry default → Topic → Card → Block → remix override,
 * merged per property. A disabled toggle or a still kind (cut / none) resolves to duration 0.
 * Unknown kind keys (never valid on write) fall back to the registry default.
 */
export function resolveMotionSettings(layers: {
	topic?: MotionSettingsV1 | null;
	card?: MotionSettingsV1 | null;
	block?: MotionSettingsV1 | null;
	remix?: MotionSettingsV1 | null;
}): ResolvedMotionSettings {
	const result = {} as ResolvedMotionSettings;
	for (const field of MOTION_FIELD_KEYS) {
		const role = MOTION_FIELD_ROLES[field];
		const defaults = MOTION_REGISTRY_DEFAULTS[field];
		let kindKey = defaults.kindKey;
		let durationMs = defaults.durationMs;
		let params: Record<string, unknown> | undefined;
		const source: ResolvedMotionField["source"] = {
			kindKey: "registry",
			durationMs: "registry",
			params: "registry",
			enabled: "registry",
		};
		let enabled = true;
		const toggle = toggleFor(field);
		for (const layer of LAYER_ORDER) {
			const settings = layers[layer];
			if (!settings) {
				continue;
			}
			if (toggle && settings[toggle] !== undefined) {
				enabled = settings[toggle] as boolean;
				source.enabled = layer;
			}
			const entry = settings[field];
			if (!entry) {
				continue;
			}
			if (
				entry.kindKey !== undefined &&
				isMotionKindKey(role, entry.kindKey)
			) {
				// Params belong to a kind: a lower layer's params don't carry over to a new kind.
				if (entry.kindKey !== kindKey) {
					params = undefined;
					source.params = "registry";
				}
				kindKey = entry.kindKey;
				source.kindKey = layer;
			}
			if (entry.durationMs !== undefined) {
				durationMs = entry.durationMs;
				source.durationMs = layer;
			}
			if (entry.params !== undefined) {
				params = entry.params;
				source.params = layer;
			}
		}
		if (!enabled) {
			kindKey = STILL_KIND_KEY[role];
		}
		const kind = getMotionKind(role, kindKey);
		const parsedParams = kind.paramsSchema.safeParse(
			params ?? kind.defaultParams,
		);
		result[field] = {
			kindKey,
			// No layer set a duration: the resolved kind's own default (typewriter 1200 ms, …).
			durationMs:
				kind.defaultDurationMs === 0
					? 0
					: source.durationMs === "registry"
						? kind.defaultDurationMs
						: durationMs,
			params: parsedParams.success
				? parsedParams.data
				: kind.defaultParams,
			source,
		};
	}
	return result;
}
